import { type DisplayUnit } from '@core';

import { formatCorridorLabel, getOffsetPercent, type CorridorExtent } from '../../../utils/corridors';

import { type RangeLabelProps } from './RangeLabel/RangeLabel';

import type { Corridor } from '../ParameterCardCorridors';

export type TwoCorridorLabelLayout = {
    system: {
        start: RangeLabelProps;
        end: RangeLabelProps;
    };
    goal: {
        start: RangeLabelProps;
        end: RangeLabelProps;
    };
    hideSystemStart: boolean;
    hideSystemEnd: boolean;
};

type LabelBounds = {
    start: number;
    end: number;
};

type CorridorLabelLayout = {
    start: RangeLabelProps;
    end: RangeLabelProps;
    bounds: {
        start: LabelBounds;
        end: LabelBounds;
    };
};

const LABEL_EDGE_OFFSET_PX = 8;
const LABEL_PROXIMITY_THRESHOLD_PX = 8;
const APPROX_LABEL_CHARACTER_WIDTH_PX = 11;
const APPROX_LABEL_PADDING_PX = 8;

type UseTwoCorridorLabelLayoutArgs = {
    systemCorridor: Corridor;
    goalCorridor: Corridor;
    extent: CorridorExtent;
    unit: DisplayUnit;
    trackWidth: number;
};

export const useTwoCorridorLabelLayout = ({
    systemCorridor,
    goalCorridor,
    extent,
    unit,
    trackWidth,
}: UseTwoCorridorLabelLayoutArgs): TwoCorridorLabelLayout => {
    const systemLabelLayout = getCorridorLabelLayout(systemCorridor, 'system', extent, unit, trackWidth);
    const goalLabelLayout = getCorridorLabelLayout(goalCorridor, 'goal', extent, unit, trackWidth);

    return {
        system: {
            start: systemLabelLayout.start,
            end: systemLabelLayout.end,
        },
        goal: {
            start: goalLabelLayout.start,
            end: goalLabelLayout.end,
        },
        hideSystemStart:
            trackWidth > 0 &&
            (rangesOverlapOrTouch(systemLabelLayout.bounds.start, goalLabelLayout.bounds.start) ||
                rangesOverlapOrTouch(systemLabelLayout.bounds.start, goalLabelLayout.bounds.end)),
        hideSystemEnd:
            trackWidth > 0 &&
            (rangesOverlapOrTouch(systemLabelLayout.bounds.end, goalLabelLayout.bounds.start) ||
                rangesOverlapOrTouch(systemLabelLayout.bounds.end, goalLabelLayout.bounds.end)),
    };
};

const getCorridorLabelLayout = (
    corridor: Corridor,
    tone: RangeLabelProps['tone'],
    extent: CorridorExtent,
    unit: DisplayUnit,
    trackWidth: number,
): CorridorLabelLayout => {
    const startPosition = getOffsetPercent(corridor.innerMin, extent);
    const endPosition = getOffsetPercent(corridor.innerMax, extent);
    const startPlacement =
        tone === 'system' ? 'system-start' : startPosition > 0 ? 'goal-outside-start' : 'goal-inside-start';
    const endPlacement = tone === 'system' ? 'system-end' : endPosition < 100 ? 'goal-outside-end' : 'goal-inside-end';

    return {
        start: {
            value: corridor.innerMin,
            unit,
            tone,
            placement: startPlacement,
            position: startPosition,
        },
        end: {
            value: corridor.innerMax,
            unit,
            tone,
            placement: endPlacement,
            position: endPosition,
        },
        bounds: {
            start: getLabelBounds(corridor.innerMin, unit, startPosition, startPlacement, trackWidth),
            end: getLabelBounds(corridor.innerMax, unit, endPosition, endPlacement, trackWidth),
        },
    };
};

const getLabelBounds = (
    value: number,
    unit: DisplayUnit,
    position: number,
    placement: NonNullable<RangeLabelProps['placement']>,
    trackWidth: number,
): LabelBounds => {
    const anchorPx = (position / 100) * trackWidth;
    const labelWidth = estimateLabelWidth(value, unit);
    const labelStartsAtAnchor = placement === 'system-start' || placement === 'goal-inside-start';

    if (labelStartsAtAnchor) {
        return {
            start: anchorPx + LABEL_EDGE_OFFSET_PX,
            end: anchorPx + LABEL_EDGE_OFFSET_PX + labelWidth,
        };
    }

    return {
        start: anchorPx - LABEL_EDGE_OFFSET_PX - labelWidth,
        end: anchorPx - LABEL_EDGE_OFFSET_PX,
    };
};

const estimateLabelWidth = (value: number, unit: DisplayUnit) => {
    const { integer, fraction, isNegative } = formatCorridorLabel(value, unit);
    const text = `${isNegative ? '-' : ''}${integer ?? ''}${fraction ?? ''}`;

    return text.length * APPROX_LABEL_CHARACTER_WIDTH_PX + APPROX_LABEL_PADDING_PX;
};

const rangesOverlapOrTouch = (first: LabelBounds, second: LabelBounds) => {
    return (
        first.start <= second.end + LABEL_PROXIMITY_THRESHOLD_PX &&
        second.start <= first.end + LABEL_PROXIMITY_THRESHOLD_PX
    );
};
